/**
 * ConnectYourAiSection — "Connect an AI chat": optional. One card with the
 * personal connection URL, how to add it to the chat, and the Easy Relay
 * Skill. The personal key and URL are shown only in MyKeySection, right
 * after create/replace; this section never renders a key value. The shared
 * workspace connection lives in WorkspaceSecuritySection (and is being
 * retired).
 *
 * Row sentences are checked against server/mcp/teamLog.ts: `author` on
 * post_message is self-reported on every connection; only a personal key
 * carries `ownerUid` (the member), which is what the Lead-only tools
 * check.
 */
import { useState } from "react";
import { CopyInstructionsButton } from "../CopyInstructionsButton";
import { buildDaemonKeyInstructions } from "../../lib/copyInstructions";
import easyRelaySkillContent from "../../../docs/relay-skill-default.md?raw";
import type { CreatedKey } from "./MyKeySection";
import { SecretField } from "./SecretField";

// track:skills-retirement-relay (decision #49035, spec #49036 Slice 2) —
// a copy-paste prompt for the human's own external AI chat, unrelated to the
// in-app skills/spawner system being retired. Exported so it is
// independently testable.
export function EasyRelaySkillPanel() {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(easyRelaySkillContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable — button label stays "Copy" so the user knows it didn't work.
    }
  };

  return (
    <div>
      <p className="tp-small" style={{ marginBottom: 8 }}>
        This is for your <strong>human planner</strong> — the AI chat you think out loud with —
        not for spawned dev agents. Paste it as a project instruction, custom GPT system prompt,
        or Gemini gem instructions. It keeps directives to the team routed through Relay instead
        of getting stuck in a chat only you can see.
      </p>
      <button className="btn primary" onClick={handleCopy}>
        {copied ? "Copied ✓" : "Copy Easy Relay Skill"}
      </button>
    </div>
  );
}

export function ConnectYourAiSection({ created = null, keyExists = false }: { created?: CreatedKey | null; keyExists?: boolean }) {
  return (
    <section id="team-section-mcp" style={{ marginBottom: 32 }}>
      <div className="sc-section-label">Connect an AI chat</div>
      <div className="tp-lede">Optional. Lets the AI chat you plan with read and post in the Log as you.</div>

      <div className="tp-card">
        <div className="tp-row">
          <div>
            <div className="tp-row-label">1 · Connection URL</div>
            <div className="tp-small">Contains your key. Acts as you.</div>
          </div>
          <div>
            {created ? (
              <SecretField key={`c-${created.mcpUrl}`} value={created.mcpUrl} testId="connection-value" />
            ) : keyExists ? (
              <div className="tp-small">Shown once, with your key. Lost it? Create a new key in My Key.</div>
            ) : (
              <div className="tp-small">Appears when you create your key.</div>
            )}
            <div className="tp-small">
              Your account is recorded on every call. Lead-only tools work if you are the Lead. The name on a post is the one the AI gives itself.
            </div>
          </div>
        </div>

        <div className="tp-row">
          <div className="tp-row-label">2 · Add it to your chat</div>
          <div>
            <div className="tp-small" style={{ marginBottom: 6 }}>
              In Claude.ai, open Connectors in Settings, choose Add custom connector, and paste the connection URL.
            </div>
            {/* track:workspace-key-onboarding (decision #48253) — never contains a key or URL —
                see src/lib/copyInstructions.ts's own header comment. */}
            <CopyInstructionsButton text={buildDaemonKeyInstructions()} label="Copy Instructions for Your AI" />
          </div>
        </div>

        <div className="tp-row">
          <div>
            <div className="tp-row-label">3 · Easy Relay Skill</div>
            <div className="tp-small">Claude.ai, Gemini, ChatGPT</div>
          </div>
          <EasyRelaySkillPanel />
        </div>
      </div>

      <div className="sc-callout warn">
        Treat the key and URLs like passwords. Don’t paste them into chats or screenshots.
      </div>
    </section>
  );
}
